import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { IonIcon } from '@ionic/react';
import { downloadOutline, trashOutline, eyeOutline, globeOutline, lockClosedOutline } from 'ionicons/icons';
import './CanvaPopover.css';

const CanvaPopover = ({
  isOpen,
  onClose,
  triggerRef,
  actions,
  mousePosition = null, // { x, y } coordinates where the popover should appear
  placement = 'auto' // auto, right, left, bottom, top
}) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const [actualPlacement, setActualPlacement] = useState(placement);
  const popoverRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Simple, stable positioning - calculate once after a brief delay
      const timer = setTimeout(() => {
        if (mousePosition) {
          // Use mouse position for right-click
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          const popoverWidth = 160;
          const popoverHeight = actions.length * 40 + 16;
          
          let left = mousePosition.x + 8;
          let top = mousePosition.y;
          
          // Keep within viewport
          if (left + popoverWidth > viewportWidth - 8) {
            left = mousePosition.x - popoverWidth - 8;
          }
          if (top + popoverHeight > viewportHeight - 8) {
            top = viewportHeight - popoverHeight - 8;
          }
          
          setPosition({ top, left });
          setActualPlacement('right');
        } else if (triggerRef?.current) {
          // Use trigger element for button click
          const rect = triggerRef.current.getBoundingClientRect();
          const popoverWidth = 160;
          const popoverHeight = actions.length * 40 + 16;
          const viewportWidth = window.innerWidth;
          const viewportHeight = window.innerHeight;
          
          // Position to the right of the trigger by default
          let left = rect.right + 8;
          let top = rect.top;
          
          // If not enough space on right, position to the left
          if (left + popoverWidth > viewportWidth - 8) {
            left = rect.left - popoverWidth - 8;
          }
          
          // Keep within viewport vertically
          if (top + popoverHeight > viewportHeight - 8) {
            top = viewportHeight - popoverHeight - 8;
          }
          if (top < 8) {
            top = 8;
          }
          
          setPosition({ top, left });
          setActualPlacement(left > rect.right ? 'right' : 'left');
        }
        
        setVisible(true);
      }, 10); // Small delay for stability

      return () => clearTimeout(timer);
    } else {
      setVisible(false);
    }
  }, [isOpen, triggerRef, mousePosition, actions.length]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(event.target) &&
        triggerRef?.current &&
        !triggerRef.current.contains(event.target)
      ) {
        onClose();
      }
    };

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    const handleScroll = () => {
      onClose();
    };

    if (isOpen) {
      // Use capture phase to ensure we catch the event before other handlers
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
      document.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', onClose);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('scroll', handleScroll, true);
      window.removeEventListener('resize', onClose);
    };
  }, [isOpen, onClose, triggerRef]);

  if (!visible) return null;

  const getActionIcon = (action) => {
    switch (action.type) {
      case 'download':
        return downloadOutline;
      case 'delete':
        return trashOutline;
      case 'view':
        return eyeOutline;
      case 'public':
        return globeOutline;
      case 'private':
        return lockClosedOutline;
      default:
        return action.icon;
    }
  };

  // Render popover using portal to avoid z-index/stacking context issues
  const popoverContent = (
    <div
      ref={popoverRef}
      className={`canva-popover ${visible ? 'canva-popover-visible' : ''}`}
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
        zIndex: 10000
      }}
      data-placement={actualPlacement}
    >
      <div className="canva-popover-content">
        {actions.map((action, index) => (
          <div
            key={index}
            className={`canva-popover-item ${action.disabled ? 'canva-popover-item-disabled' : ''}`}
            data-action={action.type}
            onClick={(e) => {
              e.stopPropagation();
              if (!action.disabled) {
                action.onClick();
                onClose();
              }
            }}
          >
            {(action.icon || action.type) && (
              <IonIcon 
                icon={getActionIcon(action)} 
                className="canva-popover-icon"
              />
            )}
            <span className="canva-popover-text">{action.label}</span>
            {action.badge && (
              <span className="canva-popover-badge">{action.badge}</span>
            )}
          </div>
        ))}
      </div>
      
      {/* Arrow pointing to trigger - only show if we have a specific target */}
      {(actualPlacement === 'right' || actualPlacement === 'left') && (
        <div className={`canva-popover-arrow canva-popover-arrow-${actualPlacement}`} />
      )}
    </div>
  );

  return ReactDOM.createPortal(popoverContent, document.body);
};

export default CanvaPopover; 